import { standardId } from '../../../utils/standardRef'

export function authorIdOf(comment) {
  const a = comment?.author
  return a && (a._id || a)
}

export function isOwnComment(comment, userId) {
  const id = authorIdOf(comment)
  return id === 'me' || (userId && String(id) === String(userId))
}

function memberHas(list, userId) {
  return (list || []).some((x) => {
    const id = x && (x._id || x)
    return id === 'me' || (userId && String(id) === String(userId))
  })
}

export function isLikedBy(comment, userId) {
  return memberHas(comment?.likes, userId)
}

export function isSavedBy(comment, userId) {
  return memberHas(comment?.saves, userId)
}

export function authorLabel(comment, user) {
  const a = comment?.author
  if (!a || a._id === 'me' || (user && String(a._id) === String(user._id))) {
    return user?.displayName || user?.email || 'You'
  }
  return a.displayName || a.email || 'Someone'
}

export function formatWhen(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// Map a comment's anchors to picker option objects (for the edit-items modal).
export function itemsToOptions(comment) {
  return (comment?.items || []).map((a) => ({
    _id: a.item || a._id,
    label: a.label,
    standard: a.standard,
    order: a.order,
    path: a.path,
    pageStart: a.pageStart,
    pageEnd: a.pageEnd,
  }))
}

// Distinct authors present in a comment list, for the author filter dropdown.
export function authorsOf(comments) {
  const byId = new Map()
  ;(comments || []).forEach((c) => {
    const a = c.author
    if (!a || !a._id || a._id === 'me') return
    if (!byId.has(String(a._id))) {
      byId.set(String(a._id), { _id: a._id, label: a.displayName || a.email || 'Someone' })
    }
  })
  return [...byId.values()].sort((x, y) => x.label.localeCompare(y.label))
}

// Map a standard's full item tree (reading order) to item-filter options, so a
// user can filter by any chapter/section — not only items that already have a
// comment. Used with the "include descendants" roll-up to catch nested threads.
export function itemTreeToOptions(items) {
  return (items || []).map((it) => ({
    _id: it._id,
    label: it.label || 'Item',
    standard: it.standard,
    order: it.order,
  }))
}

// Distinct tagged items across a comment list, for the items filter dropdown.
// Limited to `standard` when given (the open document), so the list stays
// relevant to the window. Options: { _id, label, standard, order }.
export function taggedItemsOf(comments, standard) {
  const byId = new Map()
  ;(comments || []).forEach((c) => {
    ;(c.items || []).forEach((a) => {
      if (standardId(standard) && standardId(a.standard) !== standardId(standard)) return
      const id = String(a.item || a._id || '')
      if (!id || byId.has(id)) return
      byId.set(id, { _id: a.item || a._id, label: a.label || 'Item', standard: a.standard, order: a.order })
    })
  })
  return [...byId.values()].sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
}

export function sortComments(comments, sort) {
  const list = [...comments]
  if (sort === 'oldest') {
    list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
  } else if (sort === 'likes') {
    list.sort((a, b) => (b.likes?.length || 0) - (a.likes?.length || 0))
  } else if (sort === 'least') {
    list.sort((a, b) => (a.likes?.length || 0) - (b.likes?.length || 0))
  } else {
    list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  }
  return list
}
